import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import api from "../services/api";
import refreshToken from "../utils/refreshToken";

export type OwnerSearchUser = {
  id: number;
  email: string;
  first_name?: string;
  phone?: string;
};

type UseOwnerEmailSearchOptions = {
  accessToken: string;
  dateToExpires: Date | number;
  onEmptyResults?: () => void;
  /** Minimum characters before calling the API. */
  minChars?: number;
  debounceMs?: number;
};

/**
 * Owner email typeahead with explicit open/close state so late API responses
 * cannot reopen the dropdown after select or outside click.
 */
export function useOwnerEmailSearch({
  accessToken,
  dateToExpires,
  onEmptyResults,
  minChars = 4,
  debounceMs = 350,
}: UseOwnerEmailSearchOptions) {
  const [results, setResults] = useState<OwnerSearchUser[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const seqRef = useRef(0);
  const lockedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEmptyResultsRef = useRef(onEmptyResults);
  onEmptyResultsRef.current = onEmptyResults;

  const cancelInFlight = useCallback((reason: string) => {
    abortRef.current?.abort(reason);
    abortRef.current = null;
  }, []);

  const closeDropdown = useCallback(() => {
    lockedRef.current = true;
    seqRef.current += 1;
    cancelInFlight("Owner search closed");
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setIsFetching(false);
    setIsOpen(false);
    setResults([]);
  }, [cancelInFlight]);

  const unlockSearch = useCallback(() => {
    lockedRef.current = false;
  }, []);

  const runSearch = useCallback(
    async (searchTerm: string) => {
      if (lockedRef.current) return;

      const trimmed = searchTerm.trim();
      if (trimmed.length < minChars) {
        setResults([]);
        setIsOpen(false);
        setIsFetching(false);
        return;
      }

      cancelInFlight("Superseded by newer owner search");
      const controller = new AbortController();
      abortRef.current = controller;
      const requestSeq = ++seqRef.current;

      setIsFetching(true);

      try {
        const expiresAt =
          dateToExpires instanceof Date
            ? dateToExpires
            : new Date(dateToExpires);
        const token = await refreshToken(expiresAt, accessToken);
        api.defaults.headers.authorization = `Bearer ${token ?? accessToken}`;

        const response = await api.get("/api/adm/users", {
          params: { email: trimmed },
          signal: controller.signal,
        });

        if (requestSeq !== seqRef.current || lockedRef.current) return;

        const found: OwnerSearchUser[] = response.data?.data?.users ?? [];
        if (found.length === 0) {
          onEmptyResultsRef.current?.();
          setResults([]);
          setIsOpen(false);
          return;
        }

        setResults(found);
        setIsOpen(true);
      } catch (err: unknown) {
        const canceled =
          (err as { code?: string; name?: string })?.code === "ERR_CANCELED" ||
          (err as { name?: string })?.name === "CanceledError" ||
          (err as { name?: string })?.name === "AbortError";
        if (canceled) return;
        if (requestSeq === seqRef.current && !lockedRef.current) {
          setResults([]);
          setIsOpen(false);
        }
      } finally {
        if (requestSeq === seqRef.current) {
          setIsFetching(false);
        }
      }
    },
    [accessToken, cancelInFlight, dateToExpires, minChars]
  );

  const onSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const searchTerm = e.target.value;
      lockedRef.current = false;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      if (searchTerm.trim().length < minChars) {
        seqRef.current += 1;
        cancelInFlight("Query below minimum");
        setIsFetching(false);
        setIsOpen(false);
        setResults([]);
        return searchTerm;
      }

      setIsFetching(true);
      debounceRef.current = setTimeout(() => {
        void runSearch(searchTerm);
      }, debounceMs);

      return searchTerm;
    },
    [cancelInFlight, debounceMs, minChars, runSearch]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      cancelInFlight("Owner search unmounted");
    };
  }, [cancelInFlight]);

  return {
    results,
    isFetching,
    isOpen,
    closeDropdown,
    unlockSearch,
    onSearchInputChange,
  };
}
